import {
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { baseUrl } from "../config";
import { getAuthorizedHeaders } from "../middleware";
import { getIdToken } from "./login.action";
import {
  clearSessionExpiry,
  requestSessionExtension,
  resolveSessionExtension,
  showSessionExpiredConsent,
  showSessionHardCapExpired,
} from "./session.action";
import { QueryActions } from "../models";

export const baseQuery: BaseQueryFn<
  QueryActions,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { authType, token, url } = args;
  const authHeaders = await getAuthorizedHeaders(api.dispatch, {
    authType,
    token,
    url,
  });
  if (authHeaders) args.headers = { ...args.headers, ...authHeaders };
  const baseQueryArgs = { ...args, headers: args.headers };
  let result = await fetchBaseQuery({ baseUrl })(baseQueryArgs, api, extraOptions);

  const errorCode =
    result.error && typeof result.error.data === "object" && result.error.data !== null
      ? "code" in result.error.data && typeof result.error.data.code === "string"
        ? result.error.data.code
        : undefined
      : undefined;
  if (result.error && result.error.status === 401) {
    if (errorCode === "TOKEN_EXPIRED_BY_AGE") {
      // 15-min idle expiry — show the consent screen and wait for the user.
      // No auto-refresh: the request is retried only after explicit consent.
      api.dispatch(showSessionExpiredConsent());
      const consented = await requestSessionExtension();
      api.dispatch(clearSessionExpiry());
      if (consented) {
        try {
          const freshToken = await getIdToken(true);
          if (freshToken) {
            const retryArgs = {
              ...args,
              headers: {
                ...args.headers,
                Authorization: `Bearer ${freshToken}`,
                'Content-Type': 'application/json',
              },
            };
            result = await fetchBaseQuery({ baseUrl })(retryArgs, api, extraOptions);
          }
        } catch {
          // Token refresh rejected (offline, throttled, revoked) — keep the
          // original 401 result so normal unauthorized handling still sees it.
        }
      }
    } else if (errorCode === "SESSION_HARD_CAP_EXPIRED") {
      // 2-hour absolute cap — forced logout. No consent, no refresh.
      // Settle any pending consent gate so a concurrent consent request does
      // not hang after the hard-cap screen unmounts the consent modal.
      resolveSessionExtension(false);
      api.dispatch(showSessionHardCapExpired());
    }
  }

  return result;
};
